import { c as _c } from "react/compiler-runtime";
import { useChat, useChatActions } from '@ai-sdk-tools/store';
import type { ChatOnFinishCallback } from 'ai';
import { useEffect, useRef } from 'react';
import { useAutoResume } from './use-auto-resume';
import { useChatTransport } from './use-chat-transport';
import type { UIMessageWithMetadata } from '../../../lib/types';
interface UseNovaChatOptions {
  chatUuid: string;
  initialMessages?: UIMessageWithMetadata[];
  onFinish?: ChatOnFinishCallback<UIMessageWithMetadata>;
  onError?: (error: Error) => void;
}

/**
 * Wrapper around useChat from @ai-sdk-tools/store with Nova-specific configuration.
 * Configures transport, handles stream resumability, and manages chat state.
 *
 * IMPORTANT: This hook MUST be called before any other components use useChatActions()
 * or other store hooks, as it configures the transport for the store.
 *
 * Note: Data parts are handled separately via useDataParts() hook, not through onData callback.
 */
export function useNovaChat(t0) {
  const $ = _c(17);
  const {
    chatUuid,
    initialMessages: t1,
    onFinish,
    onError
  } = t0;
  let t2;
  if ($[0] !== t1) {
    t2 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const initialMessages = t2;
  const transport = useChatTransport();
  let t3;
  if ($[2] !== chatUuid || $[3] !== initialMessages || $[4] !== onError || $[5] !== onFinish || $[6] !== transport) {
    t3 = {
      id: chatUuid,
      resume: false,
      transport,
      messages: initialMessages,
      experimental_throttle: 100,
      onFinish,
      onError
    };
    $[2] = chatUuid;
    $[3] = initialMessages;
    $[4] = onError;
    $[5] = onFinish;
    $[6] = transport;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const chat = useChat(t3);
  const chatActions = useChatActions();
  const prevChatUuidRef = useRef(chatUuid);
  const prevInitialMessagesRef = useRef(initialMessages);
  let t4;
  let t5;
  if ($[8] !== chat || $[9] !== chatActions || $[10] !== chatUuid || $[11] !== initialMessages) {
    t4 = () => {
      const chatUuidChanged = prevChatUuidRef.current !== chatUuid;
      const initialMessagesChanged = prevInitialMessagesRef.current !== initialMessages;
      if (chatUuidChanged || initialMessagesChanged) {
        const isNewChat = initialMessages.length === 0 && chatUuidChanged;
        if (isNewChat) {
          chatActions.setNewChat(chatUuid, []);
        } else {
          if (chatUuidChanged) {
            chatActions.setNewChat(chatUuid, initialMessages);
          } else {
            chat.setMessages(initialMessages);
          }
        }
        prevChatUuidRef.current = chatUuid;
        prevInitialMessagesRef.current = initialMessages;
      }
    };
    t5 = [chatUuid, initialMessages, chat, chatActions];
    $[8] = chat;
    $[9] = chatActions;
    $[10] = chatUuid;
    $[11] = initialMessages;
    $[12] = t4;
    $[13] = t5;
  } else {
    t4 = $[12];
    t5 = $[13];
  }
  useEffect(t4, t5);
  let t6;
  if ($[14] !== chat.resumeStream || $[15] !== initialMessages) {
    t6 = {
      autoResume: true,
      initialMessages,
      resumeStream: chat.resumeStream
    };
    $[14] = chat.resumeStream;
    $[15] = initialMessages;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  useAutoResume(t6);
  return chat;
}